interface Config {
    /**
     * include array indices in FormData keys
     * defaults to false
     */
    indices?: boolean;

    /**
     * treat null values like undefined values and ignore them
     * defaults to false
     */
    nullsAsUndefineds?: boolean;

    /**
     * convert true or false to 1 or 0 respectively
     * defaults to false
     */
    booleansAsIntegers?: boolean;

    /**
     * store arrays even if they're empty
     * defaults to false
     */
    allowEmptyArrays?: boolean;

    /**
     * don't include array notation in FormData keys for any Attributes excepted Files in arrays
     * defaults to false
     */
    noAttributesWithArrayNotation?: boolean;

    /**
     * don't include array notation in FormData keys for Files in arrays
     * defaults to false
     */
    noFilesWithArrayNotation?: boolean;

    /**
     * use dots instead of brackets for object notation in FormData keys
     * defaults to false
     */
    dotsForObjectNotation?: boolean;
}

function isBlob(value: any) {
    return (
        value === Object(value) &&
        typeof value.size === 'number' &&
        typeof value.type === 'string' &&
        typeof value.slice === 'function'
    );
}

function isFile(value: any) {
    return (
        isBlob(value) &&
        typeof value.name === 'string' &&
        (value.lastModifiedDate === Object(value.lastModifiedDate) ||
            typeof value.lastModified === 'number')
    );
}

export default function serialize(
    obj: any,
    cfg: Config = {},
    fd = new FormData(),
    pre = ''
) {
    cfg.indices = !!cfg.indices;
    cfg.nullsAsUndefineds = !!cfg.nullsAsUndefineds;
    cfg.booleansAsIntegers = !!cfg.booleansAsIntegers;
    cfg.allowEmptyArrays = !!cfg.allowEmptyArrays;
    cfg.noAttributesWithArrayNotation = !!cfg.noAttributesWithArrayNotation;
    cfg.noFilesWithArrayNotation = !!cfg.noFilesWithArrayNotation;
    cfg.dotsForObjectNotation = !!cfg.dotsForObjectNotation;

    if (obj === undefined) {
        return fd;
    } else if (obj === null) {
        if (!cfg.nullsAsUndefineds) {
            fd.append(pre, '');
        }
    } else if (typeof obj === 'boolean') {
        if (cfg.booleansAsIntegers) {
            fd.append(pre, (obj ? 1 : 0) as any);
        } else {
            fd.append(pre, obj as any);
        }
    } else if (Array.isArray(obj)) {
        if (obj.length) {
            obj.forEach((value, index) => {
                let key = `${pre}[${cfg.indices ? index : ''}]`;

                if (
                    cfg.noAttributesWithArrayNotation ||
                    (cfg.noFilesWithArrayNotation && isFile(value))
                ) {
                    key = pre;
                }

                serialize(value, cfg, fd, key);
            });
        } else if (cfg.allowEmptyArrays) {
            fd.append(cfg.noAttributesWithArrayNotation ? pre : `${pre}[]`, '');
        }
    } else if (obj instanceof Date) {
        fd.append(pre, obj.toISOString());
    } else if (obj === Object(obj) && !isBlob(obj)) {
        Object.keys(obj).forEach(prop => {
            const value = obj[prop];

            if (Array.isArray(value)) {
                while (
                    prop.length > 2 &&
                    prop.lastIndexOf('[]') === prop.length - 2
                ) {
                    prop = prop.substring(0, prop.length - 2);
                }
            }

            const key = pre
                ? cfg.dotsForObjectNotation
                    ? `${pre}.${prop}`
                    : `${pre}[${prop}]`
                : prop;

            serialize(value, cfg, fd, key);
        });
    } else {
        fd.append(pre, obj);
    }

    return fd;
}
